import * as XLSX from 'xlsx';

export const EXCEL_ACCEPT = '.xlsx,.xls,.csv,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel';

export function isExcelFile(file) {
  const name = String(file?.name || '').toLowerCase();
  return name.endsWith('.xlsx') || name.endsWith('.xls');
}

/** Convert Excel date serial, Date object, or text to YYYY-MM-DD */
export function normalizeExcelDate(value) {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toISOString().slice(0, 10);
  }

  const num = Number(value);
  if (!Number.isNaN(num) && num > 25569 && num < 60000) {
    const parsed = XLSX.SSF.parse_date_code(num);
    if (parsed?.y) {
      return `${parsed.y}-${String(parsed.m).padStart(2, '0')}-${String(parsed.d).padStart(2, '0')}`;
    }
  }

  const text = String(value ?? '').trim();
  if (!text) return '';

  const iso = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;

  const dmy = text.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
  if (dmy) {
    const [, d, m, y] = dmy;
    return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
  }

  return text;
}

export function normalizeImportCellValue(value) {
  if (value instanceof Date) {
    return value.toISOString().slice(0, 10);
  }
  if (typeof value === 'number') {
    return Number.isInteger(value) ? String(value) : String(value);
  }
  return value == null ? '' : String(value);
}

export function parseExcelBuffer(arrayBuffer) {
  const workbook = XLSX.read(arrayBuffer, { type: 'array', cellDates: true });
  const sheetName = workbook.SheetNames[0];
  if (!sheetName) return [];
  const sheet = workbook.Sheets[sheetName];
  const rows = XLSX.utils.sheet_to_json(sheet, { defval: '', raw: false });
  return rows.map((row) => {
    const normalized = {};
    Object.entries(row).forEach(([key, value]) => {
      normalized[key] = normalizeImportCellValue(value);
    });
    return normalized;
  });
}

function buildSheetFromColumns(columns, rows) {
  return rows.map((row) => {
    const entry = {};
    columns.forEach((col) => {
      const val = col.exportValue ? col.exportValue(row) : col.render ? '' : row[col.key];
      entry[col.label] = val == null ? '' : val;
    });
    return entry;
  });
}

export function exportToExcel(filename, columns, rows) {
  const worksheet = XLSX.utils.json_to_sheet(buildSheetFromColumns(columns, rows));
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
  const outName = filename.endsWith('.xlsx') ? filename : `${filename.replace(/\.csv$/i, '')}.xlsx`;
  XLSX.writeFile(workbook, outName);
}

/** @param {{ name: string, columns: object[], rows: object[] }[]} sheets */
export function exportWorkbookToExcel(filename, sheets = []) {
  const workbook = XLSX.utils.book_new();
  sheets.forEach(({ name, columns, rows }) => {
    const safeName = String(name || 'Sheet').slice(0, 31).replace(/[\\/?*[\]:]/g, ' ');
    const worksheet = XLSX.utils.json_to_sheet(buildSheetFromColumns(columns, rows));
    XLSX.utils.book_append_sheet(workbook, worksheet, safeName || 'Sheet');
  });
  const outName = filename.endsWith('.xlsx') ? filename : `${filename.replace(/\.csv$/i, '')}.xlsx`;
  XLSX.writeFile(workbook, outName);
}

export function downloadExcelTemplate(filename, columns, exampleRow = {}) {
  const row = {};
  columns.forEach((col) => {
    row[col.label] = exampleRow[col.key] ?? '';
  });
  exportToExcel(filename, columns.map((col) => ({ label: col.label, key: col.key })), [row]);
}

export function parseImportFile(file, parseCsvText) {
  return new Promise((resolve, reject) => {
    if (!file) {
      resolve([]);
      return;
    }

    const reader = new FileReader();

    reader.onload = (event) => {
      try {
        if (isExcelFile(file)) {
          resolve(parseExcelBuffer(event.target.result));
          return;
        }
        resolve(parseCsvText(String(event.target?.result || '')));
      } catch (error) {
        reject(error);
      }
    };

    reader.onerror = () => reject(new Error('Unable to read file'));

    if (isExcelFile(file)) {
      reader.readAsArrayBuffer(file);
    } else {
      reader.readAsText(file);
    }
  });
}
